import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import HttpApis from "../../services/HttpApis";
import "./Styles.css";

export default function ThankYouPage() {
  const [copied, setCopied] = useState(false);
  const [countdown, setCountdown] = useState(10);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Stripe round-trips this on the success_url only. A cancel/back press has
  // no session_id — so its absence is the first (cheap) signal that this isn't
  // a real completed checkout.
  const sessionId = searchParams.get("session_id");

  // Server-confirmed verification state. We never show "Payment Successful!"
  // off the URL alone — the URL only proves checkout *opened*. The real proof
  // is a paid entitlement row (written by the Stripe webhook on payment_status
  // === 'paid'). We poll content-entitlements/status until that row exists.
  //   "checking" → asking the server (webhook may still be in flight)
  //   "success"  → entitlement confirmed; safe to show success + auto-route
  //   "failed"   → no entitlement after retries (cancelled / not paid yet)
  const [verifyState, setVerifyState] = useState("checking");

  // Real Stripe receipt, resolved from the backend once ownership is confirmed.
  //   receiptId  → Stripe charge/payment id (ch_/pi_) — short, the proper
  //                transaction reference to display (the cs_ session id is long
  //                and overflows the card).
  //   receiptUrl → Stripe's hosted receipt page for the "View Receipt" link.
  const [receiptId, setReceiptId] = useState("");
  const [receiptUrl, setReceiptUrl] = useState("");

  // The transaction id we DISPLAY and COPY is the resolved Stripe receipt id
  // only. We intentionally do NOT show the long cs_ session id as a fallback:
  // it would render first and then visibly swap to the short id when the
  // receipt fetch lands ("full then short" flicker), and copying mid-swap
  // could grab the wrong/partial value. Until the receipt resolves we show a
  // short loading placeholder; copy is disabled until then so you can never
  // copy an incomplete id.
  const transactionId = receiptId || "";
  const transactionDisplay = receiptId || "Loading…";

  const getSavedContent = () => {
    try {
      const saved = localStorage.getItem("postPaymentContent");
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  };

  // For series, prefer seriesSlug so we land on the whole series page, not a specific episode
  const getDetailsPath = (content) => {
    // Fall back to /home, not "/" — root can bounce to profile-select.
    if (!content?.slug || !content?.type) return "/home";
    const slug = content.type === "series" && content.seriesSlug
      ? content.seriesSlug
      : content.slug;
    return `/details/${content.type}/${slug}`;
  };

  // The status endpoint queries by the content's own slug (the page slug), not
  // the series wrapper slug — the entitlement is keyed to the purchased item.
  const getStatusSlug = (content) => content?.slug || null;

  // Does the server's status payload show a real, owned entitlement?
  // Works for both movies and series — both return rent/buy_entitlement and
  // can_stream. Any one of them being truthy means the webhook granted access.
  const isOwned = (data) =>
    !!(data?.rent_entitlement || data?.buy_entitlement || data?.can_stream);

  // Server-authoritative verification. We poll because Stripe redirects the
  // browser to success_url and fires the webhook in PARALLEL — the entitlement
  // row may land a beat after this page loads. Bounded retries with a short
  // backoff; if it never appears, treat it as "not paid" and bounce out.
  useEffect(() => {
    let cancelled = false;

    // No session_id ⇒ not a completed checkout (cancel/back button, or a
    // direct hit). Don't even ask the server — just leave.
    if (!sessionId) {
      const content = getSavedContent();
      navigate(content ? getDetailsPath(content) : "/home", { replace: true });
      return undefined;
    }

    const content = getSavedContent();
    const slug = getStatusSlug(content);
    if (!slug) {
      // We have a session but no idea which content — can't verify ownership.
      // Don't fake success; send them somewhere safe.
      navigate("/home", { replace: true });
      return undefined;
    }

    const MAX_ATTEMPTS = 6; // ~ up to ~10s total with the backoff below
    let attempt = 0;

    const poll = async () => {
      attempt += 1;
      try {
        const res = await HttpApis.getEntitlementStatus(slug);
        const data = res?.data?.data || res?.data || {};
        if (cancelled) return;
        if (isOwned(data)) {
          setVerifyState("success");
          // Ownership confirmed → fetch the real Stripe receipt (URL + clean
          // ch_/pi_ id). Purely cosmetic: failure here just leaves the
          // fallbacks in place and never blocks the success screen.
          HttpApis.getEntitlementReceipt(sessionId)
            .then((rcp) => {
              if (cancelled) return;
              const r = rcp?.data?.data || rcp?.data || {};
              if (r.receipt_url) setReceiptUrl(r.receipt_url);
              // Use the real receipt id when present; otherwise fall back to the
              // session id so the transaction id resolves to SOMETHING copyable
              // (never stuck on "Loading…" if Stripe returns no charge id yet).
              setReceiptId(r.receipt_id || sessionId);
            })
            .catch(() => {
              // Receipt fetch failed entirely — still give the user a stable,
              // copyable reference (the session id) rather than a perpetual
              // loading state.
              if (!cancelled) setReceiptId(sessionId);
            });
          return;
        }
      } catch (err) {
        // Network/transient error — fall through to retry/give-up logic.
        console.error("entitlement status check failed:", err);
      }
      if (cancelled) return;
      if (attempt >= MAX_ATTEMPTS) {
        setVerifyState("failed");
        return;
      }
      // Linear backoff: 1s, 2s, 3s… gives the webhook time without hammering.
      setTimeout(poll, attempt * 1000);
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  // Auto-route into the content — ONLY after the server confirmed ownership.
  useEffect(() => {
    if (verifyState !== "success") return undefined;
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          const content = getSavedContent();
          navigate(getDetailsPath(content));
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [verifyState]);

  // Verification exhausted without an entitlement — payment wasn't completed
  // (or didn't go through). Never show success; return the user to the page
  // they came from so they can retry.
  useEffect(() => {
    if (verifyState !== "failed") return;
    const content = getSavedContent();
    navigate(content ? getDetailsPath(content) : "/home", { replace: true });
  }, [verifyState]);

  const handleCopy = () => {
    // Only copy a fully-resolved id — never the loading placeholder. Always
    // writes the complete transactionId string (not whatever is visually
    // rendered), so truncation/wrapping in the UI can't affect what's copied.
    if (!transactionId) return;
    navigator.clipboard.writeText(transactionId).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  const handleGoToDetails = () => {
    const content = getSavedContent();
    navigate(getDetailsPath(content));
  };

  // While verifying (or about to redirect on failure) we must NOT render the
  // success card — that's the whole bug we're fixing. Show a neutral confirming
  // state instead.
  if (verifyState !== "success") {
    return (
      <div className="thankyou-container">
        <div className="thankyou-card">
          <p className="enjoy-text">Confirming your payment…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="thankyou-container">
      <div className="thankyou-card">
        <div className="check-icon">✓</div>

        <h1>Payment Successful!</h1>
        <p className="enjoy-text">Your payment was successfully processed.</p>

        <div className="details-box">
          <p className="tran-label">Transaction ID</p>
          <div className="tran-id-row">
            <span className="tran-id">{transactionDisplay}</span>
            <div className="copy-wrapper">
              <button
                className="copy-icon"
                onClick={handleCopy}
                disabled={!transactionId}
                aria-label="Copy transaction ID"
                title={transactionId ? "Copy transaction ID" : "Resolving transaction ID…"}
                style={!transactionId ? { opacity: 0.4, cursor: "default" } : undefined}
              >
                📋
              </button>
              {copied && <span className="copied-text">Copied!</span>}
            </div>
          </div>
        </div>

        {receiptUrl && (
          <a
            href={receiptUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="receipt-link"
          >
            View Receipt
          </a>
        )}

        <button className="go-home-btn" onClick={handleGoToDetails}>
          Details Page
        </button>
      </div>
    </div>
  );
}
